import { observer } from 'mobx-react-lite'
import React from 'react'

import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { Icon, Text } from '@mxenabled/mxui'

import { CurrencyText, useGlobalCopyStore } from '../../common/'
import NetWorthChange from './NetWorthChange'
import { AccountGroup, AssetsLiabilities } from '../../core/types/NetWorth'
import { groupAccounts } from '../../core/utils/NetWorthUtils'

const Group: React.FC<AccountGroup> = ({ accounts, name, icon }) => {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <>
      <ListItem sx={(theme) => ({ backgroundColor: theme.palette.background.paper })}>
        <ListItemButton onClick={() => setIsOpen(!isOpen)}>
          {icon && (
            <ListItemIcon>
              <Icon name={icon} size={24} sx={{ mr: 12 }} />
            </ListItemIcon>
          )}
          <ListItemText
            primary={
              <Text sx={{ fontWeight: 700 }} variant="body1">
                {name}
              </Text>
            }
          />
          <ListItemIcon>
            {isOpen ? <Icon name="expand_less" size={24} /> : <Icon name="expand_more" size={24} />}
          </ListItemIcon>
        </ListItemButton>
      </ListItem>
      <Divider />
      <Collapse in={isOpen} timeout="auto" unmountOnExit={true}>
        {accounts.map((account, index) => (
          <List key={account.accountName}>
            <ListItem sx={() => ({ py: 14 })}>
              <ListItemText
                primary={
                  <Stack direction="row" justifyContent="space-between" sx={{ fontWeight: 400 }}>
                    <Text
                      sx={{
                        marginRight: 10,
                      }}
                      variant="body1"
                    >
                      {account.accountName}
                    </Text>
                    <CurrencyText
                      amount={account.balance}
                      sx={{
                        flexShrink: 0,
                      }}
                      variant="body2"
                    />
                  </Stack>
                }
                sx={{ pl: 72, pr: 16 }}
              />
            </ListItem>
            {index < accounts.length - 1 ? <Divider variant="inset" /> : <Divider />}
          </List>
        ))}
      </Collapse>
    </>
  )
}

const ListGroup: React.FC<{
  groupedAccounts: AccountGroup[]
}> = ({ groupedAccounts }) => (
  <>
    {groupedAccounts.map(({ accounts, icon, name }) => (
      <Group accounts={accounts} icon={icon} key={name} name={name} />
    ))}
  </>
)

const AssetsLiabilitiesDetail: React.FC<AssetsLiabilities> = ({
  netWorth,
  assets,
  liabilities,
  totalAssets,
  totalLiabilities,
}) => {
  const { net_worth: copy } = useGlobalCopyStore()

  const theme = useTheme()

  return (
    <>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          paddingTop: 24,
          marginBottom: 8,
        }}
      >
        <Text variant="body1">{copy.current_net_worth}</Text>
        <CurrencyText amount={netWorth?.y ?? 0} variant="h1" />
        {netWorth && (
          <NetWorthChange
            currencyLabel={copy.this_month}
            iconSize={20}
            netWorth={netWorth}
            theme={theme}
          />
        )}
      </Box>
      <List>
        <ListSubheader sx={{ mb: 8, mt: 12, mx: 8 }}>
          <ListItemText
            primary={
              <Stack direction="row" justifyContent="space-between">
                <Text sx={{ fontWeight: 700 }} variant="caption">
                  {copy.assets}
                </Text>
                <CurrencyText amount={totalAssets} sx={{ fontWeight: 400 }} variant="caption" />
              </Stack>
            }
          />
        </ListSubheader>
        <ListGroup groupedAccounts={groupAccounts(assets)} />
        <ListSubheader sx={{ mb: 8, mt: 12, mx: 8 }}>
          <ListItemText
            primary={
              <Stack direction="row" justifyContent="space-between">
                <Text sx={{ fontWeight: 700 }} variant="caption">
                  {copy.liabilities}
                </Text>
                <CurrencyText
                  amount={totalLiabilities}
                  sx={{ fontWeight: 400 }}
                  variant="caption"
                />
              </Stack>
            }
          />
        </ListSubheader>
        <ListGroup groupedAccounts={groupAccounts(liabilities)} />
      </List>
    </>
  )
}

export default observer(AssetsLiabilitiesDetail)
